import React from 'react';

import {
  type CaraWorkspaceFieldsFragment,
  CaraWorkspaceMessageRole,
  CaraWorkspaceState
} from '@oysterjs/ais/api/graphql';
import { getStepTitle } from '../lib/step-title';

export type WorkspaceRunStatus =
  /** Workspace has never run a plan stage. Bar should show nothing for status. */
  | 'never_run'
  /** Workspace is actively processing. `elapsedMs` ticks once per second. */
  | 'working'
  /**
   * Workspace finished its most recent run. `elapsedMs` is the final duration
   * of that run.
   */
  | 'done';

export type WorkspaceRunTimer = {
  status: WorkspaceRunStatus;
  /**
   * Working: live-updated milliseconds since the most recent plan stage started.
   * Done: the duration of the most recent plan stage.
   * Never run: `null`.
   */
  elapsedMs: number | null;
  /**
   * What the agent is currently doing, mirroring the title shown in the latest
   * stage-event group's compact row: the in-progress event's title, or
   * "Writing response..." once the reply is streaming, or "Thinking..."
   * between events. Only populated while `status === 'working'`; `null` otherwise.
   */
  currentStep: string | null;
};

const computeBaseTimer = (
  workspace: CaraWorkspaceFieldsFragment,
  now: number
): WorkspaceRunTimer => {
  const lastStage = workspace.planStages[workspace.planStages.length - 1];
  if (!lastStage) return { status: 'never_run', elapsedMs: null, currentStep: null };

  const stageStart = new Date(lastStage.createdAt).getTime();

  // While Cara is working, surface live elapsed time even if the most recent
  // stage has already ended (a brief gap between stages should still feel "live").
  if (workspace.state === CaraWorkspaceState.Working) {
    const inProgress = lastStage.events.find((e) => !e.endedAt);
    // Mirror the conversation's compact-row logic exactly so the top-bar title
    // stays in lockstep with what's shown inside the latest stage-event group.
    // - In-progress event present → its title.
    // - Else if the agent has started streaming its reply → "Writing response...".
    // - Else → "Thinking..." (between events / before the first event).
    // Falling back to the most-recent finished event's title (the previous
    // behaviour) caused a visible lag where the bar advertised a stale step
    // while the conversation already showed "Thinking..." or the next step.
    let currentStep: string;
    if (inProgress) {
      currentStep = getStepTitle(inProgress) ?? 'Working...';
    } else if (isWritingResponse(workspace)) {
      currentStep = 'Writing response...';
    } else {
      currentStep = 'Thinking...';
    }
    return { status: 'working', elapsedMs: Math.max(0, now - stageStart), currentStep };
  }

  if (lastStage.endedAt) {
    const stageEnd = new Date(lastStage.endedAt).getTime();
    return { status: 'done', elapsedMs: Math.max(0, stageEnd - stageStart), currentStep: null };
  }

  // Stage with no endedAt but workspace is not working: best-effort fallback.
  return { status: 'done', elapsedMs: Math.max(0, now - stageStart), currentStep: null };
};

/**
 * Whether the agent has started streaming its response for the current turn —
 * detected by the last message in the workspace being authored by the agent.
 * If the most recent message is from the user, the agent hasn't begun its
 * reply yet (it's still planning / running tools).
 */
const isWritingResponse = (workspace: CaraWorkspaceFieldsFragment): boolean => {
  const messages = workspace.messages ?? [];
  const lastMessage = messages[messages.length - 1];
  return lastMessage?.role === CaraWorkspaceMessageRole.Agent;
};

/**
 * Tracks the current run state for the top-bar status pill. While the workspace
 * is `Working`, ticks once per second so the displayed elapsed time stays live;
 * when not working, returns the final duration of the most recent plan stage so
 * the bar can show "Took X" without further re-renders.
 */
export function useWorkspaceRunTimer(
  workspace: CaraWorkspaceFieldsFragment | undefined
): WorkspaceRunTimer {
  const isWorking = workspace?.state === CaraWorkspaceState.Working;
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (!isWorking) return undefined;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isWorking]);

  return React.useMemo(() => {
    if (!workspace) return { status: 'never_run', elapsedMs: null, currentStep: null };
    return computeBaseTimer(workspace, now);
  }, [workspace, now]);
}

/** Format a duration in ms as a compact "Xh Ym", "Xm Ys", or "Xs" string. */
export const formatElapsed = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (totalMinutes < 60) return `${totalMinutes}m ${seconds}s`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m`;
};
